import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Calendar, Users, Award, Check } from "lucide-react";

const NAVY = "#1E2761";
const ICE = "#CADCFC";
const GOLD = "#C9A227";

const THAILAND_IMAGE = "https://customer-assets-4nw71qhi.emergentagent.net/job_7e4e61dd-6912-4af3-ac17-762c1ac12aa9/artifacts/m0jeflii_TK6_2813.jpg";

export default function Programs() {
  return (
    <section
      id="programs"
      className="border-t"
      style={{ backgroundColor: "#F8F9FC", borderColor: `${NAVY}15` }}
      data-testid="programs-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">

        {/* Section Header */}
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10" style={{ backgroundColor: GOLD }} />
            <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>
              Programs
            </p>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl leading-tight mb-4" style={{ color: NAVY }}>
            Global convenings for the next generation of leaders.
          </h2>
          <p className="text-base text-gray-600 max-w-2xl">
            From flagship diplomatic conferences to ambassador networks, GYP builds spaces
            where young leaders shape international dialogue.
          </p>
        </div>

        {/* Barcelona 2027 — HERO CARD */}
        <div
          className="mb-8 overflow-hidden shadow-xl"
          style={{ backgroundColor: NAVY }}
          data-testid="program-barcelona-2027"
        >
          <div className="grid lg:grid-cols-5">
            {/* Left content */}
            <div className="lg:col-span-3 p-10 lg:p-14 text-white flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <span className="px-3 py-1 text-[9px] tracking-[0.3em] uppercase font-medium" style={{ backgroundColor: GOLD, color: NAVY }}>
                    Flagship · Now Open
                  </span>
                  <span className="text-[10px] tracking-[0.25em] uppercase text-white/60">
                    January 2027
                  </span>
                </div>
                <p className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>
                  International Leadership &amp; Diplomacy
                </p>
                <h3 className="font-serif text-4xl lg:text-5xl leading-[1.1] mb-6">
                  Barcelona 2027
                </h3>
                <p className="text-white/70 text-base leading-relaxed mb-8 max-w-lg">
                  Fifty delegates. Twenty-plus countries. Three days of negotiation, diplomacy,
                  and international collaboration in one of Europe&rsquo;s great cities.
                </p>

                <div className="grid grid-cols-3 gap-6 py-6 border-y border-white/15 mb-8">
                  <MetaItem icon={<Calendar />} label="Dates" value="Jan 15–18, 2027" />
                  <MetaItem icon={<MapPin />} label="Venue" value="Barcelona, Spain" />
                  <MetaItem icon={<Users />} label="Cohort" value="50 delegates" />
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mb-8">
                  <IncludeItem>Full 3-night accommodation</IncludeItem>
                  <IncludeItem>All meals &amp; sessions</IncludeItem>
                  <IncludeItem>Local transport in Barcelona</IncludeItem>
                  <IncludeItem>Visa invitation letter</IncludeItem>
                </div>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-6 pt-4">
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 mb-1">Starting from</p>
                  <p className="font-serif text-3xl" style={{ color: GOLD }}>
                    $20 <span className="text-sm text-white/60 font-sans">application</span>
                  </p>
                </div>
                <Link to="/register">
                  <button
                    className="px-8 py-4 text-xs tracking-[0.25em] uppercase font-medium flex items-center gap-3 transition-all hover:gap-4"
                    style={{ backgroundColor: GOLD, color: NAVY }}
                    data-testid="barcelona-apply-btn"
                  >
                    Register Now
                    <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                </Link>
              </div>
            </div>

            {/* Right visual */}
            <div className="lg:col-span-2 relative min-h-[300px] lg:min-h-full order-first lg:order-last">
              <img
                src="https://images.pexels.com/photos/34759875/pexels-photo-34759875.jpeg"
                alt="Barcelona 2027"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(to left, transparent 40%, ${NAVY}CC 100%)` }}
              />
              {/* Corner mark */}
              <div className="absolute top-6 right-6 flex items-center gap-2 text-white/80">
                <Award className="h-4 w-4" style={{ color: GOLD }} strokeWidth={1.5} />
                <span className="text-[9px] tracking-[0.3em] uppercase">Barcelona · ES</span>
              </div>
            </div>
          </div>
        </div>

        {/* Ambassadors + Thailand — 2 column secondary row */}
        <div className="grid md:grid-cols-2 gap-6">

          {/* Ambassadors */}
          <div
            className="bg-white border p-8 lg:p-10 flex flex-col justify-between"
            style={{ borderColor: `${NAVY}22` }}
            data-testid="program-ambassadors"
          >
            <div>
              <div className="flex items-center gap-2 mb-5">
                <span className="h-2 w-2 rounded-full animate-pulse" style={{ backgroundColor: "#10b981" }} />
                <span className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "#10b981" }}>
                  Open · Rolling Basis
                </span>
              </div>
              <p className="text-[11px] tracking-[0.3em] uppercase mb-2 text-gray-500">
                Global Network
              </p>
              <h3 className="font-serif text-2xl lg:text-3xl mb-3" style={{ color: NAVY }}>
                Ambassadors Program
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                Represent GYP in your country and city. Build the local chapter, connect
                youth to global opportunities.
              </p>
              <div className="space-y-2 mb-8">
                <MiniItem>Official recognition &amp; certificate</MiniItem>
                <MiniItem>Global ambassadors network</MiniItem>
                <MiniItem>Training &amp; leadership support</MiniItem>
              </div>
            </div>
            <Link to="/apply">
              <button
                className="w-full py-3 text-xs tracking-[0.25em] uppercase font-medium border-2 transition-colors hover:text-white group flex items-center justify-center gap-2"
                style={{ borderColor: NAVY, color: NAVY }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = NAVY}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                data-testid="ambassador-apply-btn"
              >
                Become Ambassador
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </Link>
          </div>

          {/* Thailand — COMPLETED */}
          <div
            className="border overflow-hidden flex flex-col"
            style={{ borderColor: `${NAVY}22`, backgroundColor: "white" }}
            data-testid="program-thailand-2026"
          >
            <div className="relative h-56 overflow-hidden">
              <img
                src={THAILAND_IMAGE}
                alt="Thailand 2026 — Successfully Concluded"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(to top, rgba(30,39,97,0.85) 0%, rgba(30,39,97,0.2) 60%, transparent 100%)" }}
              />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 text-[9px] tracking-[0.3em] uppercase text-white bg-black/40 backdrop-blur">
                  Successfully Concluded
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <p className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: GOLD }}>
                  Bangkok · June 2026
                </p>
                <h3 className="font-serif text-2xl leading-tight">
                  International Leadership Program
                </h3>
              </div>
            </div>
            <div className="p-8 flex-1 flex flex-col justify-between">
              <div>
                <p className="text-sm text-gray-600 leading-relaxed mb-6">
                  Held at the United Nations Conference Centre with 330+ delegates from
                  over 60 countries — an unforgettable convening of young leaders.
                </p>
                <div className="grid grid-cols-2 gap-4 pb-4 border-b" style={{ borderColor: `${NAVY}15` }}>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">Venue</p>
                    <p className="text-sm" style={{ color: NAVY }}>UN Conference Centre</p>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-1">Delegates</p>
                    <p className="text-sm" style={{ color: NAVY }}>330+ from 60 nations</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] tracking-[0.3em] uppercase pt-4 text-gray-400">
                Applications Closed · Archived
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

// ---------- Small subcomponents ----------
const MetaItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="mt-0.5 shrink-0" style={{ color: GOLD }}>
      {icon && <span className="[&>svg]:h-4 [&>svg]:w-4">{icon}</span>}
    </div>
    <div>
      <p className="text-[9px] tracking-[0.25em] uppercase text-white/50">{label}</p>
      <p className="text-sm mt-0.5">{value}</p>
    </div>
  </div>
);

const IncludeItem = ({ children }) => (
  <div className="flex items-start gap-2 text-sm text-white/80">
    <Check className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color: GOLD }} strokeWidth={2.5} />
    <span>{children}</span>
  </div>
);

const MiniItem = ({ children }) => (
  <div className="flex items-start gap-2 text-sm text-gray-700">
    <Check className="h-3.5 w-3.5 mt-1 shrink-0" style={{ color: GOLD }} strokeWidth={2.5} />
    <span>{children}</span>
  </div>
);
